import { Session } from "next-auth"
import { Api } from "../typings/api"
import { getSession } from "next-auth/react"
import { DEFAULT_WORKSPACE_SUBDOMAIN, resolveWorkspaceHost } from "../utils/workspaceHost"
import { loginPathReturningTo, signOutToCurrentHost } from "../utils/auth"

const SESSION_CHECK_INTERVAL = 1000 * 10 // 10 seconds
let lastSessionCheck: number
let session: Session | null
let tokenPromise: Promise<string | undefined> | null = null

export const setInitialSession = (initialSession: Session) => {
  session = initialSession
  lastSessionCheck = Date.now()
}

export const getToken = async () => {
  if (!tokenPromise || Date.now() - lastSessionCheck > SESSION_CHECK_INTERVAL) {
    tokenPromise = (async () => {
      session = await getSession()
      lastSessionCheck = Date.now()

      return session?.jwt
    })()
  }

  return tokenPromise
}

const getWorkspaceSubdomain = () => {
  // This client is imported by a handful of server-side call sites (see
  // pages/api/auth/[...nextauth].ts and the getStaticProps-based marketing
  // pages) in addition to the browser. `window` doesn't exist there, so
  // resolving a subdomain from it is meaningless anyway - those call sites
  // must supply their own X-Workspace-Subdomain header explicitly (via
  // RequestParams) rather than rely on this interceptor.
  if (typeof window === "undefined") {
    return undefined
  }

  const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN as string
  const resolution = resolveWorkspaceHost(
    window.location.hostname.toLowerCase(),
    rootDomain
  )
  return resolution.kind === "workspace" ? resolution.subdomain : undefined
}

export const client = new Api({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
})

client.instance.interceptors.request.use(
  async (config) => {
    const token = await getToken()

    if (token) {
      // @ts-ignore
      config.headers.Authorization = `Bearer ${token}`
    }

    const subdomain = getWorkspaceSubdomain()
    if (subdomain) {
      // @ts-ignore
      config.headers["X-Workspace-Subdomain"] = subdomain
    }

    return config
  },
  (error) => {
    Promise.reject(error)
  }
)

client.instance.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    //Token is expired make user login
    if (error?.response?.status === 401) {
      signOutToCurrentHost(
        loginPathReturningTo(window.location.pathname + window.location.search)
      )
    }

    // A logged-in user visiting a workspace they don't belong to - turn the
    // raw 403 into a redirect to the join page instead of an unhandled
    // AxiosError crashing the page (see workspace-resolver.ts's
    // "workspace_membership_required" message, added in Phase 2 Task 1).
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.error?.message === "workspace_membership_required" &&
      typeof window !== "undefined" &&
      window.location.pathname !== "/workspace/join"
    ) {
      window.location.href = "/workspace/join"
    }

    // The host's subdomain doesn't resolve to any real workspace (e.g. a
    // stale or made-up subdomain) - workspace-resolver.ts 404s this call
    // before ever reaching the workspace controller. Bounce to the default
    // workspace's home instead of leaving the page rendered with no
    // workspace context.
    // The bare/marketing root domain 404s here too (it has no subdomain at
    // all, by design - see workspace-resolver.ts), which is NOT an
    // unresolvable-workspace case: it must be excluded, or an anonymous
    // visitor there gets redirected to the default workspace, bounced back
    // by middleware's anonymous-workspace-root guard, and loops forever
    // (#397).
    if (
      error?.response?.status === 404 &&
      error?.config?.url?.includes("/workspaces/current") &&
      typeof window !== "undefined"
    ) {
      const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN as string
      const defaultHost = `${DEFAULT_WORKSPACE_SUBDOMAIN}.${rootDomain}`
      const resolution = resolveWorkspaceHost(
        window.location.hostname.toLowerCase(),
        rootDomain
      )
      if (
        resolution.kind === "workspace" &&
        window.location.hostname.toLowerCase() !== defaultHost
      ) {
        const port = window.location.port ? `:${window.location.port}` : ""
        window.location.href = `${window.location.protocol}//${defaultHost}${port}/`
      }
    }

    // eslint-disable-next-line
    console.error(error)
    return Promise.reject(error)
  }
)

export default client
